import React, { forwardRef, useEffect, useRef, useState } from "react";
import SelectInput from "@/components/select-input/select-input.tsx";
import { useTranslation } from "react-i18next";
import TimeSelector from "@/components/custom/time-selector.tsx";
import { CronCalendar } from "@/components/ui/cron-calendar.tsx";
import cronstrue from "cronstrue";
import { LuCalendar } from "react-icons/lu";
import { cn } from "@/lib/utils";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group.tsx";
import { DEFAULT_CRON } from "@/lib/cron-defaults";

interface CronInputProps {
  disabled?: boolean;
  className?: string;
  value?: string;
  onChange?: (value: string) => void;
  onClick?: React.MouseEventHandler<HTMLDivElement>;
}

// Exported so callers can use the same "daily at 8am" default in their own form defaultValues
// instead of duplicating the literal - keeps the two in lockstep if this default ever changes.
export { DEFAULT_CRON };

// Derived from DEFAULT_CRON rather than written out again, so the fallbacks used when a field has
// no usable value can't drift from the default the form starts on.
const [DEFAULT_MINUTE, DEFAULT_HOUR] = DEFAULT_CRON.split(" ");
// `time` is held as "<hour> <minute>", the same shape parseCron produces and TimeSelector emits.
const DEFAULT_TIME = `${DEFAULT_HOUR} ${DEFAULT_MINUTE}`;
const DEFAULT_WEEKDAY = "MON";
const DEFAULT_MONTH_DAY = 1;
// Must stay one of the minute values the hourly toggle group offers, or the seeded value would
// select nothing and put us back where we started.
const DEFAULT_MINUTE_OF_HOUR = "0";

const CronInput = forwardRef<HTMLDivElement, CronInputProps>(
  (
    { disabled = false, className = "", value: _value, onChange, onClick },
    ref,
  ) => {
    const propValue = _value || DEFAULT_CRON;
    const [frequency, setFrequency] = useState<string>();
    const [days, setDays] = useState<(number | string)[]>([]);
    const [time, setTime] = useState<string | undefined>();
    const [minuteOfHour, setMinuteOfHour] = useState<string>();
    const [value, setValue] = useState<string>(propValue);
    const [initialized, setInitialized] = useState<boolean>(false);
    const { t } = useTranslation();

    const frequencyOptions = [
      { value: "hourly", label: t("hourly") },
      { value: "daily", label: t("daily") },
      { value: "weekly", label: t("weekly") },
      { value: "monthly", label: t("monthly") },
    ];

    const dayOptions = [
      { value: "MON", label: t("monday") },
      { value: "TUE", label: t("tuesday") },
      { value: "WED", label: t("wednesday") },
      { value: "THU", label: t("thursday") },
      { value: "FRI", label: t("friday") },
      { value: "SAT", label: t("saturday") },
      { value: "SUN", label: t("sunday") },
    ];

    const getValue = () => {
      // Hourly has no hour/day/month/weekday of its own - only the minute matters, so it's
      // returned directly rather than falling through the shared hour/day/weekday building below.
      if (frequency === "hourly") {
        return `${minuteOfHour ?? "0"} * * * *`;
      }

      // Empty segments are filtered out because "".split(/[:\s]/) yields [""], not [] - element 0
      // is an empty string rather than undefined, so a plain destructuring default never fired and
      // an unset time produced an empty hour and a malformed 4-field cron ("0  * * *"). time is
      // unset exactly when the value being edited is hourly (hourly has no hour to parse), which is
      // why switching hourly -> daily/weekly/monthly used to emit an invalid schedule.
      const [hour = DEFAULT_HOUR, minute = DEFAULT_MINUTE] = (time ?? "")
        .trim()
        .split(/[:\s]/)
        .filter((part) => part.length > 0);

      let day = "*";
      const month = "*";
      let weekday = "*";

      // `days` holds weekday names for weekly and dates for monthly, so each branch takes only the
      // entries of its own shape - a leftover from the other mode would land in the wrong cron
      // field ("0 8 MON * *"). changeFrequency keeps this from happening in practice; these
      // fallbacks make it impossible to emit a cron that contradicts the chosen frequency.
      switch (frequency) {
        case "daily":
          break;

        case "weekly":
          weekday =
            days
              .filter((d) => dayOptions.some((o) => o.value === d))
              .join(",") || DEFAULT_WEEKDAY;
          break;

        case "monthly":
          // "L" (last day of the month) is kept alongside the dates - it is what CronCalendar's
          // "set as last day" checkbox produces. Number.isInteger rather than typeof, because
          // typeof NaN === "number" and a NaN would be emitted as the literal string "NaN".
          day =
            days.filter((d) => d === "L" || Number.isInteger(d)).join(",") ||
            String(DEFAULT_MONTH_DAY);
          break;

        default:
          break;
      }
      return `${minute} ${hour} ${day} ${month} ${weekday}`;
    };

    // Switching frequency has to reconcile the state the new frequency depends on, because `days`
    // means different things per frequency (weekday names vs dates) and `time` is unset after
    // parsing an hourly cron. Without this the emitted cron didn't match the frequency the user
    // picked: hourly -> anything produced an invalid cron, weekly <-> monthly carried the wrong
    // field type across, and weekly/monthly with nothing selected silently emitted a *daily*
    // schedule. Seeding here (rather than only defaulting inside getValue) keeps the inputs showing
    // the same schedule that will be saved.
    const changeFrequency = (next: string) => {
      setFrequency(next);

      // Every frequency seeds whatever sub-option it renders, so the user never lands on a
      // frequency with nothing selected. minuteOfHour in particular is only set by parseCron when
      // the incoming value was already hourly, so switching *to* hourly used to show an empty
      // toggle group while getValue()'s `?? "0"` quietly saved :00.
      if (next === "hourly") {
        if (!minuteOfHour) setMinuteOfHour(DEFAULT_MINUTE_OF_HOUR);
      } else if (!time) {
        setTime(DEFAULT_TIME);
      }

      if (next === "weekly") {
        const weekdays = days.filter((d) =>
          dayOptions.some((o) => o.value === d),
        );
        setDays(weekdays.length > 0 ? weekdays : [DEFAULT_WEEKDAY]);
      } else if (next === "monthly") {
        const dates = days.filter((d) => d === "L" || Number.isInteger(d));
        setDays(dates.length > 0 ? dates : [DEFAULT_MONTH_DAY]);
      } else {
        setDays([]);
      }
    };

    const parseCron = (cron: string) => {
      const [minute = "", hour = "", day = "", , weekday = ""] =
        cron.split(" ");

      setTime(
        hour === "*" || minute === "*" || (hour == "" && minute == "")
          ? undefined
          : [hour, minute].join(" "),
      );

      if (weekday !== "*" && weekday?.length > 0) {
        setFrequency("weekly");
        const daysArray = weekday.split(",").map((d) => d);
        setDays(daysArray);
      } else if (day !== "*" && day?.length > 0) {
        setFrequency("monthly");
        // The day-of-month field can hold "L" (last day of the month) alongside numeric dates, so
        // tokens are mapped individually instead of being run through parseInt: parseInt("L") is
        // NaN, and because typeof NaN === "number" that NaN survived every filter and came back out
        // of getValue as the literal string "NaN" ("0 5 NaN,1,31 * *"). Anything that is neither
        // "L" nor a whole number is dropped rather than carried through as garbage.
        setDays(
          day
            .split(",")
            .map((token) => token.trim())
            .map((token) => (token === "L" ? "L" : Number(token)))
            .filter((d): d is number | "L" => d === "L" || Number.isInteger(d)),
        );
      } else if (hour === "*" && minute !== "*" && minute.length > 0) {
        setFrequency("hourly");
        setMinuteOfHour(minute);
        setDays([]);
      } else {
        setFrequency("daily");
        setDays([]);
      }
    };

    // parseCron (below) sets time/frequency/days/minuteOfHour from the incoming value on mount,
    // which are exactly the deps this effect watches - without this flag, parsing the *existing*
    // value on mount looks identical to a genuine user edit, firing onChange (and for consumers
    // that save on change, an unwanted network request) before the user has touched anything.
    const skipNextNotifyRef = useRef(false);

    useEffect(() => {
      if (initialized) {
        if (skipNextNotifyRef.current) {
          skipNextNotifyRef.current = false;
          return;
        }
        const newValue = getValue();
        setValue(newValue);
        onChange?.(newValue); // Notify parent of value change
      }
    }, [time, days, frequency, minuteOfHour]);

    useEffect(() => {
      if (!initialized) {
        parseCron(propValue);
        setValue(propValue);
        skipNextNotifyRef.current = true;
        setInitialized(true);
      }
    }, [propValue, initialized]);

    const renderFrequencyOptions = () => {
      switch (frequency) {
        case "hourly":
          return (
            <div className={"grid grid-cols-[15%_auto] gap-2 items-center"}>
              <label>{t("atMinute")}</label>
              <ToggleGroup
                type="single"
                value={minuteOfHour}
                onValueChange={(v) => v && setMinuteOfHour(v)}
                disabled={disabled}
                className="justify-start"
              >
                <ToggleGroupItem value="0">:00</ToggleGroupItem>
                <ToggleGroupItem value="15">:15</ToggleGroupItem>
                <ToggleGroupItem value="30">:30</ToggleGroupItem>
                <ToggleGroupItem value="45">:45</ToggleGroupItem>
              </ToggleGroup>
            </div>
          );

        case "daily":
          return (
            <div className={"grid grid-cols-[15%_auto] gap-2 items-center"}>
              <label>{t("at")}</label>
              <TimeSelector
                value={time}
                onChange={(newTime) => setTime(newTime)}
                disabled={disabled}
              />
            </div>
          );

        case "weekly":
          return (
            <>
              <div className={"grid grid-cols-[15%_auto] gap-2 items-center"}>
                <label>{t("on")}</label>
                <SelectInput
                  options={dayOptions}
                  value={dayOptions.filter((option) =>
                    days.includes(option.value),
                  )}
                  // A weekly schedule with no day selected has no cron representation - it would
                  // emit "* " in the weekday field, i.e. a daily schedule under a "weekly" label.
                  // Snapping back to the default keeps what is shown and what is saved identical.
                  onChange={(selected) =>
                    setDays(
                      selected.length > 0
                        ? selected.map((o) => o.value)
                        : [DEFAULT_WEEKDAY],
                    )
                  }
                  isMulti={true}
                />
              </div>
              <div className="flex flex-col">
                <div className={"grid grid-cols-[15%_auto] gap-2 items-center"}>
                  <label>{t("at")}</label>
                  <TimeSelector
                    value={time}
                    onChange={(newTime) => setTime(newTime)}
                    disabled={disabled}
                  />
                </div>
              </div>
            </>
          );

        case "monthly":
          return (
            <>
              <label>{t("selectDates")}:</label>
              <CronCalendar
                // "L" is included, not filtered out: it is part of the selection the calendar owns,
                // and dropping it here meant a saved last-day schedule came back with the checkbox
                // unchecked - the cron said "L" while the UI showed nothing, and the next edit
                // silently discarded it.
                selectedDays={days.filter(
                  (d): d is number | "L" => d === "L" || Number.isInteger(d),
                )}
                // Same reasoning as the weekly day picker: an empty date selection has no monthly
                // cron representation and would silently mean "every day".
                onChange={(newDays) =>
                  setDays(newDays.length > 0 ? newDays : [DEFAULT_MONTH_DAY])
                }
                disabled={disabled}
              />
              <div className={"grid grid-cols-[15%_auto] gap-2 items-center"}>
                <label>{t("at")}</label>
                <TimeSelector
                  value={time}
                  onChange={(newTime) => setTime(newTime)}
                  disabled={disabled}
                />
              </div>
            </>
          );

        default:
          return null;
      }
    };

    return (
      <>
        {disabled ? (
          <div
            className="rounded-md border border-input bg-transparent px-3 py-1 shadow-sm h-10 flex justify-between items-center opacity-50"
            ref={ref}
          >
            {cronstrue.toString(value, { verbose: true })}
            <LuCalendar className="ml-2" />
          </div>
        ) : (
          <div
            className={cn("flex flex-col gap-2", className)}
            onClick={onClick}
            ref={ref}
          >
            <div className={"grid grid-cols-[15%_auto] gap-2 items-center"}>
              <label className={"mr-2"}>{t("sent")}</label>
              <SelectInput
                options={frequencyOptions}
                value={frequencyOptions.find((x) => x.value === frequency)}
                onChange={(o) => changeFrequency(o?.value || "daily")}
                isMulti={false}
                isDisabled={disabled}
              ></SelectInput>
            </div>
            {renderFrequencyOptions()}
          </div>
        )}
      </>
    );
  },
);

export default CronInput;
